import React, { useState, useEffect, useMemo } from 'react';
import { Button, ButtonGroup, Modal, Page } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  FormErrors,
  WifiConfiguration,
  FormMeta,
  Policy
} from '../types/interface';
import { FormState } from '../types/interface';
import { MDM_ASSET_CLASS_IDS } from '../Constants';
import { WIFI_FORM_CONFIG } from './Metas/WifiMeta';
import GenericForm from './GenericForm';
import { clearHiddenFieldValues } from '../utils/MdmUtils';
import { validateFormMetaForm } from '../utils/validationUtils';
import { FormattedMessage } from 'react-intl';
import { getTranslatedText } from '@nile/components';

interface WifiModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    wifi: WifiConfiguration,
    isEdit: boolean,
    originalWifi?: WifiConfiguration
  ) => void;
  classId: string;
  selectedWifi?: WifiConfiguration | null;
  policyObj?: Policy;
  intl?: any; // Internationalization object
}

function WifiModal(props: WifiModalProps) {
  const {
    isOpen,
    onClose,
    onSave,
    classId,
    selectedWifi,
    policyObj,
    intl = {}
  } = props;

  const isEdit = selectedWifi !== null;
  const [wifiForm, setWifiForm] = useState<FormState>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const formFields = useMemo<FormMeta[]>(() => {
    if (classId === MDM_ASSET_CLASS_IDS.ANDROID) {
      return WIFI_FORM_CONFIG.android.fields;
    } else if (
      classId === MDM_ASSET_CLASS_IDS.APPLE_IOS ||
      classId === MDM_ASSET_CLASS_IDS.APPLE_IPAD_OS
    ) {
      return WIFI_FORM_CONFIG.ios.fields;
    }
    // Default to iOS fields
    return WIFI_FORM_CONFIG.ios.fields;
  }, [classId]);

  // Initialize form values from field defaults
  const initializeWifiForm = (): FormState => {
    const initialValues: FormState = {};

    formFields.forEach((field) => {
      if (isEdit && selectedWifi) {
        initialValues[field.name] =
          selectedWifi[field.name as keyof WifiConfiguration] ?? null;
      } else {
        initialValues[field.name] = field.defaultValue ?? null;
      }
    });

    return initialValues;
  };

  const handleFormChange = (
    fieldName: string,
    value: string | number | boolean
  ) => {
    let processedValue: string | number | boolean | null = value;
    if (typeof value === 'string' && value.trim() === '') {
      processedValue = null;
    }

    setWifiForm((prev) => ({
      ...prev,
      [fieldName]: processedValue
    }));
  };

  // Use common validation functions from utils
  const validateForm = (wifiFormValues: FormState): FormErrors => {
    return validateFormMetaForm(formFields, wifiFormValues, intl);
  };

  // Dynamic mapping utility using FormMeta
  const mapFormToWifiConfig = (
    wifiFormValues: FormState
  ): WifiConfiguration => {
    // When policy is inherited, don't set configId to treat as new item
    const isInheritedPolicy = policyObj?.inherited;

    const wifiConfig: Partial<WifiConfiguration> = {
      configId:
        isEdit && selectedWifi && !isInheritedPolicy
          ? selectedWifi.configId
          : `temp_${Date.now()}_${Math.random()}`,
      // For inherited policies, always mark as new item when editing or adding
      isNewItem: isInheritedPolicy || !isEdit
    };

    // Dynamic mapping using FormMeta field definitions
    formFields.forEach((field) => {
      const formValue = wifiFormValues[field.name]; // Form state uses field.name as key
      const apiFieldName = field.name as keyof WifiConfiguration;
      wifiConfig[apiFieldName] = formValue as any;
    });

    return wifiConfig as WifiConfiguration;
  };

  const handleModalSave = async () => {
    // Clear hidden fields based on conditions before validation and save
    const clearedFormValues = clearHiddenFieldValues(wifiForm, formFields);

    setWifiForm(clearedFormValues);

    // Run validation and set errors on cleared form values
    const validationErrors = validateForm(clearedFormValues);
    setFormErrors(validationErrors);

    // Check if there are any validation errors
    const hasErrors = Object.keys(validationErrors).length > 0;
    if (hasErrors) {
      console.error('Form has validation errors:', validationErrors);
      return;
    }

    try {
      // Use dynamic mapping
      const wifiConfig = mapFormToWifiConfig(clearedFormValues);

      onSave(wifiConfig, isEdit, selectedWifi || undefined);
      handleModalCancel();
    } catch (error) {
      console.error('Error saving wifi configuration:', error);
    }
  };

  const handleModalCancel = () => {
    setWifiForm(initializeWifiForm());
    setFormErrors({});
    onClose();
  };

  // Initialize form when modal opens or when editing
  useEffect(() => {
    if (isOpen) {
      setWifiForm(initializeWifiForm());
      setFormErrors({});
    }
  }, [isOpen, selectedWifi]);

  // Reinitialize form when fields change (e.g., classId changes)
  useEffect(() => {
    if (isOpen && !selectedWifi) {
      setWifiForm(initializeWifiForm());
    }
  }, [formFields, isOpen, selectedWifi]);

  return (
    <Modal
      open={isOpen}
      onClose={handleModalCancel}
      showCloseButton={false}
      noScroll
      medium
    >
      <div className="no-scroll fh">
        <Page
          title={
            isEdit
              ? getTranslatedText(intl, 'mdm.wifi.modal.title.edit')
              : getTranslatedText(intl, 'mdm.wifi.modal.title.add')
          }
          contentHeight="100px"
          primaryAction={
            <ButtonGroup spacing="loose">
              <Button onClick={handleModalCancel} plainSecondary>
                <FormattedMessage id="common.close" />
              </Button>
              <Button
                primary
                onClick={handleModalSave}
                icon={<SVGIcon name={'Save'} />}
              >
                <FormattedMessage id="common.save" />
              </Button>
            </ButtonGroup>
          }
          variant="primary"
        >
          <div className="fh">
            <div className="mt-m-20">
              <GenericForm
                intl={intl}
                fields={formFields}
                formValues={wifiForm}
                formErrors={formErrors}
                onChange={handleFormChange}
                disabled={false}
                showDeviceTypeBadges={false}
              />
            </div>
          </div>
        </Page>
      </div>
    </Modal>
  );
}

export default WifiModal;
